import React, { useEffect, useState } from 'react';
import { Modal, Button, message, Table } from 'antd';
import { useQuery } from '@apollo/client';
import {
    GET_AVAILABLE_TASKS_FOR_TRANSFER,
    AvailableTaskForTransfer
} from '../graphql/queries/getAvailableTasksForTransfer';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import DateFilterItem from '../../../delivery/components/DateFilterItem';
import {getSmartLabelClassName} from "../../../../utils/labelWidthUtils";
import {usePickupData} from "../contexts/PickupDataContext";
interface TransferTaskModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (targetTaskId: string) => void;
    currentTaskId: string;
    warehouseId: number;
    taskDate: string;
}

const TransferTaskModal: React.FC<TransferTaskModalProps> = ({
    visible,
    onClose,
    onConfirm,
    currentTaskId,
    warehouseId,
    taskDate
}) => {
    const {drivers} = usePickupData();
    const {t, i18n} = useTranslation();
    const { vehicleTypes } = useVehicleTypes();
    const driverOptions = drivers.map((driver) => ({
        label: driver.driverName,
        value: driver.accountId.toString()
    }));
    const labelDriver = t('pickup.pickupTask.filters.driver');
    const labelTaskDate = String(t('pickup.pickupTask.filters.taskDate'));
    const labelZipcode = String(t('pickup.pickupTask.filters.zipcode') || '邮编');
    const col3Labels = [labelDriver, labelTaskDate, labelZipcode];
    // 筛选状态
    const [filterStatus, setFilterStatus] = useState<string>('ALL');
    const [filterDate, setFilterDate] = useState<string>(taskDate);
    const [filterDriverId, setFilterDriverId] = useState<string | undefined>(undefined);

    const {data, loading, error} = useQuery(GET_AVAILABLE_TASKS_FOR_TRANSFER, {
        variables: {
            warehouseId,
            taskDate: filterDate,
            excludeTaskId: Number(currentTaskId)
        },
        skip: !visible || !warehouseId || !filterDate,
        fetchPolicy: 'network-only'
    });

    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) {
            setSelectedTaskId(null);
            setFilterStatus('ALL');
            setFilterDate(taskDate);
            setFilterDriverId(undefined);
        }
    }, [visible, taskDate]);

    if (error) {
        message.error(t('pickup.pickupTask.transferTaskModal.pleaseSelectTaskDate') || '');
        return null;
    }

    // 根据状态和司机筛选数据
    const filteredData = (data?.getAvailableTasksForTransfer || []).filter((task: AvailableTaskForTransfer) => {
        // 状态筛选
        if (filterStatus !== 'ALL' && task.status !== filterStatus) {
            return false;
        }
        // 司机筛选
        if (filterDriverId) {
            const selectedDriver = drivers.find(d => d.accountId.toString() === filterDriverId);
            if (selectedDriver && task.driverName !== selectedDriver.driverName) {
                return false;
            }
        }
        return true;
    });

    const handleConfirm = () => {
        if (!selectedTaskId) {
            message.warning(t('pickup.pickupTask.transferTaskModal.pleaseSelectTask') || '');
            return;
        }
        onConfirm(selectedTaskId);
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-purple-500 mr-3"></div>
                    <span className="text-lg font-medium">{t('pickup.pickupTask.transferTaskModal.title') || ''}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1000}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t('components.button.cancel') || ''}
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirm}
                    disabled={!selectedTaskId}
                >
                    {t('components.button.ok') || ''}
                </Button>
            ]}
        >
            {/* Filter conditions */}
            <div className="mb-4 grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={String(t('pickup.pickupTask.transferTaskModal.taskStatus') || '')}
                        options={[
                            { label: String(t('components.input.all') || 'All'), value: 'ALL' },
                            { label: 'Draft', value: 'DRAFT' },
                            { label: 'Published', value: 'PUBLISHED' },
                            { label: 'In Progress', value: 'IN_PROGRESS' }
                        ]}
                        value={filterStatus}
                        onChange={(value: string) => setFilterStatus(value || 'ALL')}
                        className="w-full"
                        spanClassName="min-w-20 !font-medium"
                        showSearch
                    />
                </div>
                <div className="min-w-0">
                    <DateFilterItem
                        label={String(t('pickup.pickupTask.transferTaskModal.taskDate') || '')}
                        value={filterDate ? dayjs(filterDate) : null}
                        disabled
                        format="YYYY/MM/DD"
                        className="!flex-row !items-center"
                        spanClassName="min-w-20 !font-medium !mb-0"
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('pickup.pickupTask.filters.driver')}
                        options={driverOptions}
                        value={filterDriverId}
                        onChange={(value: string) => setFilterDriverId(value || undefined)}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                        className="w-full"
                        showSearch
                        placeholder={t('pickup.pickupTask.placeholders.selectDriver') || undefined}
                    />
                </div>
            </div>

            {/* Task table */}
            <Table
                loading={loading}
                dataSource={filteredData}
                pagination={false}
                size="small"
                rowKey="id"
                locale={{ emptyText: t('pickup.pickupTask.transferTaskModal.noAvailableTasks') || '' }}
                rowClassName={(record: AvailableTaskForTransfer) =>
                    selectedTaskId === record.id.toString() ? 'bg-purple-50' : ''
                }
                onRow={(record: AvailableTaskForTransfer) => ({
                    onClick: () => setSelectedTaskId(record.id.toString()),
                    className: 'cursor-pointer hover:bg-gray-50'
                })}
                columns={[
                    {
                        title: '',
                        dataIndex: 'selection',
                        width: 40,
                        render: (_, record: AvailableTaskForTransfer) => (
                            <div
                                className="w-4 h-4 border-2 rounded flex items-center justify-center cursor-pointer"
                                style={{
                                    borderColor: selectedTaskId === record.id.toString() ? '#8b5cf6' : '#d1d5db',
                                    backgroundColor: selectedTaskId === record.id.toString() ? '#8b5cf6' : 'transparent'
                                }}
                            >
                                {selectedTaskId === record.id.toString() && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                            </div>
                        )
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.taskNumber') || '',
                        dataIndex: 'taskNumber',
                        key: 'taskNumber',
                        render: (text) => <span className="font-medium">{text}</span>
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.driver') || '',
                        dataIndex: 'driverName',
                        key: 'driverName',
                        align: 'left',
                        render: (text): string => text || '-'
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.vehicle') || '',
                        dataIndex: 'vehicleTypeId',
                        key: 'vehicleTypeId',
                        align: 'left',
                        render: (vehicleTypeId: number) => {
                            const vt = vehicleTypes?.find(v => String(v.id) === String(vehicleTypeId));
                            const name = vt ? vt.nameEn : undefined;
                            return name || '-';
                        }
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.appointments') || '',
                        dataIndex: 'appointmentCount',
                        key: 'appointmentCount',
                        align: 'left'
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.quantity') || '',
                        dataIndex: 'quantityByUnit',
                        key: 'quantityByUnit',
                        align: 'left',
                        render: (text): string => text || '-'
                    },
                    {
                        title: t('pickup.pickupTask.transferTaskModal.loadRate') || '',
                        dataIndex: 'loadRate',
                        key: 'loadRate',
                        align: 'left',
                        render: (text) => <span className="font-medium">{text || '-'}</span>
                    }
                ]}
            />
        </Modal>
    );
};

export default TransferTaskModal; 